// src/Pages/PartnerAccount/Ticket.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ipAddress } from '../../../config';
import './Ticket.css';

const Ticket = () => {
  const partnerId = localStorage.getItem('partnerId');
  const [unredeemedTickets, setUnredeemedTickets] = useState([]);
  const [redeemedTickets, setRedeemedTickets] = useState([]);
  const [unredeemedPagination, setUnredeemedPagination] = useState({});
  const [redeemedPagination, setRedeemedPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState({});
  const [error, setError] = useState('');
  const [pageSize, setPageSize] = useState(5);
  const [unredeemedPage, setUnredeemedPage] = useState(1);
  const [redeemedPage, setRedeemedPage] = useState(1);

  // Fetch tickets function
  const fetchTickets = async (redeemed, page = 1) => {
    try {
      const response = await axios.post(`${ipAddress}/api/ContactUsRoutes/GetTicketsByPartner`, {
        partnerId,
        page,
        limit: pageSize,
        redeemed
      });

      return {
        tickets: response.data.tickets || [],
        pagination: response.data.pagination || {}
      };
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
      throw err;
    }
  };

  // Load tickets
  const loadTickets = async () => {
    setLoading(true);
    setError('');
    try {
      const [unredeemedData, redeemedData] = await Promise.all([
        fetchTickets(false, unredeemedPage),
        fetchTickets(true, redeemedPage)
      ]);

      setUnredeemedTickets(unredeemedData.tickets);
      setUnredeemedPagination(unredeemedData.pagination);
      setRedeemedTickets(redeemedData.tickets);
      setRedeemedPagination(redeemedData.pagination);
    } catch (err) {
      setError('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  // Initial load and when dependencies change
  useEffect(() => {
    if (partnerId) {
      loadTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId, pageSize, unredeemedPage, redeemedPage]);

  // Handle redeem ticket
  const handleRedeem = async (ticketId) => {
    setRedeeming(prev => ({ ...prev, [ticketId]: true }));
    try {
      await axios.post(`${ipAddress}/api/ContactUsRoutes/RedeemTicket`, {
        ticketId,
        partnerId
      });

      // Refresh tickets after redemption
      await loadTickets();
      
      alert('Ticket redeemed successfully!');
    } catch (err) {
      console.error('Failed to redeem ticket:', err);
      const errorMsg = err.response?.data?.message || 'Failed to redeem ticket';
      alert(errorMsg);
    } finally {
      setRedeeming(prev => ({ ...prev, [ticketId]: false }));
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setUnredeemedPage(1);
    setRedeemedPage(1);
  };

  // Render pagination controls
  const renderPagination = (pagination, currentPage, setPage, label) => {
    if (!pagination || pagination.totalPages <= 1) return null;

    return (
      <div className="pagination-controls">
        <span className="pagination-info">
          {label}: Page {currentPage} of {pagination.totalPages} 
          ({pagination.totalTickets} total tickets)
        </span>
        <div className="pagination-buttons">
          <button
            onClick={() => setPage(currentPage - 1)}
            disabled={!pagination.hasPrevPage}
            className="pagination-btn"
          >
            Previous
          </button>
          
          {/* Page numbers */}
          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
            .filter(page => 
              page === 1 || 
              page === pagination.totalPages || 
              Math.abs(page - currentPage) <= 1
            )
            .map(page => (
              <button
                key={page}
                onClick={() => setPage(page)}
                className={`pagination-btn ${page === currentPage ? 'active' : ''}`}
              >
                {page}
              </button>
            ))
          }
          
          <button
            onClick={() => setPage(currentPage + 1)}
            disabled={!pagination.hasNextPage}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Render ticket table
  const renderTicketTable = (tickets, title, showRedeemButton = false) => (
    <div className="ticket-table-section">
      <h3>{title} ({tickets.length})</h3>
      {tickets.length === 0 ? (
        <p className="no-tickets">No tickets found</p>
      ) : (
        <div className="table-container">
          <table className="tickets-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Restaurant</th>
                <th>Points</th>
                <th>Created Date</th>
                {showRedeemButton && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {tickets.map(ticket => (
                <tr key={ticket._id}>
                  <td>{ticket.userID}</td>
                  <td>{ticket.restoId}</td>
                  <td>{ticket.points || 0}</td>
                  <td>{ticket.date ? new Date(ticket.date).toLocaleDateString() : 'N/A'}</td>
                  {showRedeemButton && (
                    <td>
                      <button
                        onClick={() => handleRedeem(ticket._id)}
                        disabled={redeeming[ticket._id]}
                        className="redeem-btn"
                      >
                        {redeeming[ticket._id] ? 'Redeeming...' : 'Redeem'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  if (loading) return <div className="loading">Loading tickets...</div>;
  if (error) return <div className="error" style={{ color: 'red' }}>{error}</div>;

  return (
    <div className="tickets-container">
      <h2>Ticket Management</h2>
      
      {/* Page size selector */}
      <div className="controls">
        <label htmlFor="pageSize">Tickets per page:</label>
        <select
          id="pageSize"
          value={pageSize}
          onChange={(e) => handlePageSizeChange(Number(e.target.value))}
          className="page-size-select"
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
        
        <button onClick={loadTickets} className="refresh-btn">
          Refresh
        </button>
      </div>

      {/* Unredeemed Tickets */}
      {renderTicketTable(unredeemedTickets, 'Unredeemed Vouchers', true)}
      {renderPagination(unredeemedPagination, unredeemedPage, setUnredeemedPage, 'Unredeemed')}

      <div className="section-divider"></div>

      {/* Redeemed Tickets */}
      {renderTicketTable(redeemedTickets, 'Redeemed Vouchers', false)}
      {renderPagination(redeemedPagination, redeemedPage, setRedeemedPage, 'Redeemed')}
    </div>
  );
};

export default Ticket;